<template>
  <VContainer id="filter" class="mt-2" item-list-id="filter">
    <div class="v-row">
      <div
        class="v-col-12 v-col-lg-3"
        v-if="
          $vuetify.display.lgAndUp && (loading || (hasResults && hasFilters))
        "
      >
        <TheFilterSideBar />
      </div>
      <div
        class="v-col-12"
        :class="
          loading || (hasResults && hasFilters)
            ? 'v-col-lg-9'
            : 'v-col-lg-8 offset-lg-2'
        "
      >
        <div
          class="v-row sticky-top bg-light-beige z-3"
          v-if="
            loading ||
            hasResults ||
            !$vuetify.display.lgAndUp ||
            activeAttributes?.length > 0
          "
        >
          <div class="v-col-12">
            <TheFilterTopBar
              class="filter-bar mb-2"
              v-if="$vuetify.display.lgAndUp && (loading || hasResults)"
              :index-from="indexFrom"
              :index-to="indexTo"
            />
            <TheFilterMobile v-else-if="!$vuetify.display.lgAndUp" />
            <div
              v-if="!error && activeAttributes?.length > 0"
              class="attribute-options d-flex pl-3 pl-md-0 pb-3 pb-md-1"
            >
              <TheFilterAppliedOptionsBar />
            </div>
          </div>
        </div>
        <transition name="fade">
          <HeroTile
            v-if="heroData.product_id && heroData.campaign_id"
            v-show="!loading"
            :product-data="heroData"
            class="mt-3"
          />
        </transition>
        <LoadingOverlay
          width="70"
          height="70"
          :use-background="false"
          class="position-relative my-3"
          v-show="loading && !error"
        />
        <div v-if="hasResults">
          <transition name="fade">
            <!-- Stays mounted (dimmed) while loading so a slow fetch shows
                 the previous results instead of a blank page (VIP-12032). -->
            <div
              id="product-list"
              v-if="totalPages"
              :class="{ 'product-list-loading': loading }"
            >
              <TheFilterProductList
                :offers-visible="totalPages === currentPage"
                @changeSupplier="onPageChange(1)"
              />
            </div>
          </transition>
          <VSheet
            v-if="totalPages > 1"
            :loading="loading"
            class="d-flex align-center justify-center py-3"
          >
            <VPagination
              v-model="currentPage"
              :length="totalPages"
              @update:modelValue="changePage"
              :total-visible="$vuetify.display.smAndUp ? 5 : 1"
            />
          </VSheet>
        </div>
        <div v-else-if="!loading">
          <transition name="fade">
            <NoResults class="mt-md-3" />
          </transition>
        </div>
      </div>
    </div>
  </VContainer>
</template>

<script>
import { PRODUCTS_PER_PAGE } from '@/constants/CategoryPageConstants'
import { useSearchConfig } from '@/stores/searchConfig'
import { mapActions, mapState } from 'pinia'
import { defineAsyncComponent } from 'vue'
import { useGoTo } from 'vuetify'

const LoadingOverlay = defineAsyncComponent(
  () => import('@/components/basic/atoms/LoadingOverlay.vue')
)
const TheFilterAppliedOptionsBar = defineAsyncComponent(
  () => import('@/components/category/molecules/TheFilterAppliedOptionsBar.vue')
)
const TheFilterProductList = defineAsyncComponent(
  () => import('@/components/category/molecules/TheFilterProductList.vue')
)
const TheFilterSideBar = defineAsyncComponent(
  () => import('@/components/category/organisms/TheFilterSideBar.vue')
)
const TheFilterTopBar = defineAsyncComponent(
  () => import('@/components/category/molecules/TheFilterTopBar.vue')
)
const TheFilterMobile = defineAsyncComponent(
  () => import('@/components/category/organisms/TheFilterMobile.vue')
)
const NoResults = defineAsyncComponent(
  () => import('@/components/category/molecules/NoResults.vue')
)
const HeroTile = defineAsyncComponent(
  () => import('@/components/category/molecules/HeroTile.vue')
)

const SUPPLIER_VICAMPO = 'VICAMPO'
const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const { default: MessageHelper } =
      await import('@/services/helpers/MessageHelper')
    cache = { MessageHelper }
    return cache
  }
})()

export default {
  components: {
    NoResults,
    LoadingOverlay,
    TheFilterMobile,
    TheFilterSideBar,
    TheFilterProductList,
    TheFilterAppliedOptionsBar,
    HeroTile,
    TheFilterTopBar
  },
  data() {
    return {
      currentPage: 1
    }
  },
  computed: {
    ...mapState(useSearchConfig, {
      activeAttributes: 'activeAttributes',
      loading: 'categoryIsLoading',
      category: 'category',
      config: 'getConfig',
      products: 'getProducts'
    }),
    error() {
      return this.config.error
    },
    hasSupplierVicampo() {
      const { supplier } = this.config.filters
      if (!supplier) return true
      return Object.keys(supplier).includes(SUPPLIER_VICAMPO)
    },
    heroData() {
      const { hero_data: data } = this.category

      if (this.isSearch) {
        return {}
      }

      // The BE returns `hero_data` only when the configured hero still
      // matches the page conditions + active filters, so trust it: if it's
      // there, render the hero tile. We still respect the supplier filter
      // (no Vicampo supplier means no Vicampo hero).
      if (
        !this.hasSupplierVicampo ||
        !(data?.product_id && data?.campaign_id)
      ) {
        return {}
      }

      // Full mapped /v2 search doc (searchConfig hero_data) — HeroTile
      // renders straight from it when the structured price object is present.
      return data
    },
    totalPages() {
      return this.config.totalPages
    },
    indexFrom() {
      if (this.indexTo > PRODUCTS_PER_PAGE) {
        return (this.currentPage - 1) * PRODUCTS_PER_PAGE + 1
      }
      return this.indexTo ? 1 : 0
    },
    indexTo() {
      return Math.min(
        this.currentPage * PRODUCTS_PER_PAGE,
        this.config.totalCount
      )
    },
    hasResults() {
      return this.config.totalCount > 0
    },
    hasFilters() {
      return (this.category?.attributes?.length || 0) > 0
    },
    searchTerm() {
      return this.config.query?.original
    },
    hasActiveAttributes() {
      return (
        Object.keys(this.config.filters || {}).length > 0 || !!this.searchTerm
      )
    },
    isSearch() {
      return !!this.$route.query.search
    }
  },
  methods: {
    ...mapActions(useSearchConfig, [
      'fetchForCurrentRoute',
      'updateCategory',
      'applyFilter'
    ]),
    async errorCallback(e) {
      console.error(e)
      const { MessageHelper } = await _getModules()
      await MessageHelper.addErrorMessage(
        'unknown_error',
        null,
        5000,
        e,
        'category_filter_load_failed'
      )
    },
    async onPageChange(value) {
      try {
        await this.changePage(value)
        await this.handleUpdate()
      } catch (e) {
        await this.errorCallback(e)
      }
    },
    async handleUpdate() {
      // Dispatch by route so collection pages and the global search page
      // refresh through their own fetchers instead of the category endpoint.
      await this.fetchForCurrentRoute()
    },
    async changePage(value) {
      this.updateCategory({ group: 'pagination_page', value, immediate: false })
      this.currentPage = value
      this.applyFilter()
      this.goTo('#filter')
    }
  },
  setup() {
    const goTo = useGoTo()
    return { goTo }
  },
  async created() {
    this.currentPage = +this.$route.query.pagination_page || 1
  }
}
</script>
<style scoped>
.attribute-options {
  min-height: 60px;
  overflow-x: scroll;
  flex-wrap: nowrap;
  margin: 0 -15px;
}

.display-md-and-up .attribute-options {
  overflow-x: initial;
  flex-wrap: wrap;
  margin: 0;
}

.loading {
  min-height: 70px;
}

#product-list {
  transition: opacity 0.3s var(--transition-timing-cb-vicampo);
}

.product-list-loading {
  opacity: 0.5;
  pointer-events: none;
}

.fade-enter-active {
  transition: opacity 1s var(--transition-timing-cb-vicampo) 0.5s;
}

.fade-leave-active {
  transition: opacity 0.5s var(--transition-timing-cb-vicampo);
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
